import { FileText, Image as ImageIcon, FileSpreadsheet, File, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Attachment {
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
}

interface MessageAttachmentsProps {
  attachments: Attachment[];
}

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return ImageIcon;
  if (type.includes('pdf')) return FileText;
  if (type.includes('spreadsheet') || type.includes('csv')) return FileSpreadsheet;
  return File;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const MessageAttachments = ({ attachments }: MessageAttachmentsProps) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 mt-2">
      {attachments.map((attachment, index) => {
        const Icon = getFileIcon(attachment.fileType);
        const isImage = attachment.fileType.startsWith('image/');

        return (
          <div
            key={index}
            className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg border border-border"
          >
            {isImage ? (
              <a
                href={attachment.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0"
              >
                <img
                  src={attachment.fileUrl}
                  alt={attachment.fileName}
                  className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                />
              </a>
            ) : (
              <Icon className="w-5 h-5 text-muted-foreground shrink-0" />
            )}

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{attachment.fileName}</p>
              <p className="text-xs text-muted-foreground">
                {formatFileSize(attachment.fileSize)}
              </p>
            </div>

            <Button
              size="icon"
              variant="ghost"
              className="shrink-0 h-8 w-8"
              asChild
            >
              <a
                href={attachment.fileUrl}
                download={attachment.fileName}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="h-4 w-4" />
              </a>
            </Button>
          </div>
        );
      })}
    </div>
  );
};
